import { action, computed, observable } from "mobx";
import moment from "moment";
import Immersive3dTourStatus from "~/constants/Immersive3dTour/Immersive3dTourStatus";
import Immersive3dTourPropertyType from "~/constants/Immersive3dTour/Immersive3dTourPropertyType";
import Immersive3dTourSource from "~/constants/Immersive3dTour/Immersive3dTourSource";

class Immersive3dTour {
  /**
   * @property {integer} tourId Primary Key (1:1 with Tour)
   */
  @observable tourId;

  @observable providerId;

  @observable panoramasCount;

  @observable panoramasData;

  @observable newPanoramasData;

  @observable uploadArea;

  @observable configurationsUrl;

  @observable isProcessed;

  @observable isReady;

  @observable isCreation;

  @observable creationDts;

  @observable confirmedDts;

  @observable processedDts;

  @observable deliveredDts;

  @observable thumbnail;

  @observable configuration;

  @observable imagesAlignmentConfidenceLevel;

  @observable demoLink;

  @observable isDenied;

  @observable deniedMessage;

  @observable floorPlanUpdateRequestDts;

  @observable floorPlanLastUpdateDts;

  @observable propertyType;

  @observable pricingMethod;

  @observable exportRequestDts;

  @observable exportLastDts;

  @observable exportUrl;

  @observable displayTitle;

  @observable notes;

  @observable status;

  /**
   * Set while the processing job failed for lack of credits, null otherwise.
   * @property {{stage: string, requiredCredits: number|null, missingCredits: number}|null}
   */
  @observable waitingForCredits = null;

  @observable immersive3dTourUrl;

  @observable isFloorPlanFree;

  @observable hasFloorPlan;

  @observable replacePanoramaRequestDts;

  @observable replacePanoramaLastUpdateDts;

  @observable source = Immersive3dTourSource.MANUAL;

  @observable panoramasEnhancedDts;

  /**
   * @property {boolean}
   */
  @observable isUiMeet3dActive = false;

  @observable estimatedShootSize = null;

  @observable floorPlanSize = null;

  constructor(data) {
    this.setData(data);
  }

  @action
  setData(data) {
    const {
      tourId,
      providerId,
      panoramasCount,
      panoramasData,
      newPanoramasData,
      uploadArea,
      configurationsUrl,
      isProcessed,
      isReady,
      isCreation,
      creationDts,
      confirmedDts,
      processedDts,
      deliveredDts,
      thumbnail,
      configuration,
      imagesAlignmentConfidenceLevel,
      demoLink,
      isDenied,
      deniedMessage,
      displayTitle,
      notes,
      status,
      immersive3dTourUrl,
      floorPlanUpdateRequestDts,
      floorPlanLastUpdateDts,
      propertyType,
      pricingMethod,
      exportRequestDts,
      exportLastDts,
      exportUrl,
      isFloorPlanFree,
      hasFloorPlan,
      replacePanoramaRequestDts,
      replacePanoramaLastUpdateDts,
      source,
      panoramasEnhancedDts,
      isUiMeet3dActive,
      estimatedShootSize,
      floorPlanSize,
      waitingForCredits,
    } = data;
    this.tourId = tourId;
    this.providerId = providerId;
    this.panoramasCount = panoramasCount;
    this.panoramasData = panoramasData;
    this.newPanoramasData = newPanoramasData;
    this.uploadArea = uploadArea;
    this.configurationsUrl = configurationsUrl;
    this.isProcessed = isProcessed;
    this.isReady = isReady;
    this.isCreation = isCreation;
    this.creationDts = creationDts ? moment.unix(Number(creationDts)) : null;
    this.confirmedDts = confirmedDts ? moment.unix(Number(confirmedDts)) : null;
    this.processedDts = processedDts ? moment.unix(Number(processedDts)) : null;
    this.deliveredDts = deliveredDts ? moment.unix(Number(deliveredDts)) : null;
    this.thumbnail = thumbnail;
    this.configuration = configuration;
    this.imagesAlignmentConfidenceLevel = imagesAlignmentConfidenceLevel;
    this.demoLink = demoLink;
    this.isDenied = isDenied;
    this.deniedMessage = deniedMessage;
    this.displayTitle = displayTitle;
    this.notes = notes;
    this.status = status;
    this.immersive3dTourUrl = immersive3dTourUrl;
    this.floorPlanUpdateRequestDts = floorPlanUpdateRequestDts ? moment.unix(Number(floorPlanUpdateRequestDts)) : null;
    this.floorPlanLastUpdateDts = floorPlanLastUpdateDts ? moment.unix(Number(floorPlanLastUpdateDts)) : null;
    this.propertyType = propertyType;
    this.pricingMethod = pricingMethod;
    this.exportRequestDts = exportRequestDts ? moment.unix(Number(exportRequestDts)) : null;
    this.exportLastDts = exportLastDts ? moment.unix(Number(exportLastDts)) : null;
    this.exportUrl = exportUrl;
    this.isFloorPlanFree = isFloorPlanFree;
    this.hasFloorPlan = hasFloorPlan;
    this.replacePanoramaRequestDts = replacePanoramaRequestDts ? moment.unix(Number(replacePanoramaRequestDts)) : null;
    this.replacePanoramaLastUpdateDts = replacePanoramaLastUpdateDts
      ? moment.unix(Number(replacePanoramaLastUpdateDts))
      : null;
    this.source = source;
    this.panoramasEnhancedDts = panoramasEnhancedDts ? moment.unix(Number(panoramasEnhancedDts)) : null;
    this.isUiMeet3dActive = isUiMeet3dActive;
    this.estimatedShootSize = estimatedShootSize ? parseInt(estimatedShootSize, 10) : null;
    this.floorPlanSize = floorPlanSize ? parseFloat(floorPlanSize) : null;
    this.waitingForCredits = waitingForCredits || null;
  }

  @computed
  get hasNewPanoramasData() {
    if (this.newPanoramasData === null) {
      return false;
    }

    return this.newPanoramasData.length > 0;
  }

  @computed
  get floorPlanUpdateRequestTimeoutDts() {
    if (this.floorPlanUpdateRequestDts === null) {
      return null;
    }

    const floorPlanUpdateRequestDts = this.floorPlanUpdateRequestDts.clone();

    return floorPlanUpdateRequestDts.add(5, "minutes");
  }

  @computed
  get isResidential() {
    return this.propertyType === Immersive3dTourPropertyType.RESIDENTIAL;
  }

  @computed
  get isCommercial() {
    return this.propertyType === Immersive3dTourPropertyType.COMMERCIAL;
  }

  @computed
  get hasPropertyType() {
    return Object.values(Immersive3dTourPropertyType).indexOf(this.propertyType) !== -1;
  }

  @computed
  get hasActiveReplacePanoramaRequest() {
    return (
      this.replacePanoramaRequestDts !== null && this.replacePanoramaRequestDts > this.replacePanoramaLastUpdateDts
    );
  }

  @computed
  get shortDisplayTitle() {
    return (this.displayTitle || "").split(/[,.]+/)[0];
  }

  @computed
  get isActive() {
    return this.status !== Immersive3dTourStatus.DENIED;
  }

  @computed
  get clientFacingStatus() {
    switch (this.status) {
      case Immersive3dTourStatus.PROCESSING:
        return "processing";
      case Immersive3dTourStatus.POST_PROCESSING:
        return "postProcessing";
      case Immersive3dTourStatus.DELIVERED:
        return "delivered";
      case Immersive3dTourStatus.NOT_IN_CREATION:
        return "notInCreation";
      case Immersive3dTourStatus.DENIED:
        return "denied";
      case Immersive3dTourStatus.INACTIVE:
        return "inactive";
      default:
        return "inCreation";
    }
  }

  /**
   * Gets the thumbnail for the default size (large).
   */
  @computed
  get processedThumbnail() {
    return this.isProcessed ? this.thumbnail : null;
  }

  @computed
  get processedThumbnailSmall() {
    return this.isProcessed ? `${this.thumbnail}?size=768x480` : null;
  }

  @action
  setDisplayTitle(displayTitle) {
    this.displayTitle = displayTitle;
  }
}

export default Immersive3dTour;
